const STORAGE_KEY = 'googleAuthRedirect';
const MAX_AGE_MS = 15 * 60 * 1000;

export function useGoogleAuthRedirect() {
  function getRedirectUri() {
    return `${useRequestURL().origin}/auth/google`;
  }

  function begin(returnTo) {
    const nonce = crypto.randomUUID();

    write({
      nonce,
      returnTo: safeReturnPath(returnTo),
      createdAt: Date.now(),
    });

    return nonce;
  }

  function completeRedirect(nonce, code) {
    const pending = read();
    if (!isFresh(pending) || pending.nonce !== nonce) return null;

    const next = {
      ...pending,
      resume: true,
      code: typeof code === 'string' && code ? code : null,
    };

    write(next);

    return next;
  }

  function peekResume() {
    const pending = read();
    if (!pending?.resume || !isFresh(pending)) return null;

    return pending;
  }

  function consumeResume() {
    const pending = peekResume();
    write(null);

    return pending;
  }

  return {
    getRedirectUri,
    begin,
    completeRedirect,
    peekResume,
    consumeResume,
  };
}

export function shouldUseGoogleRedirect() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent || '';
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;

  return window.matchMedia('(max-width: 767px)').matches;
}

function safeReturnPath(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    return '/';
  }

  if (path.startsWith('/auth/google')) return '/';

  return path;
}

function isFresh(value) {
  return Boolean(value?.createdAt) && Date.now() - value.createdAt <= MAX_AGE_MS;
}

function read() {
  if (!import.meta.client) return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(value) {
  if (!import.meta.client) return;

  try {
    if (!value) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage can be unavailable in private mode.
  }
}
